import { useEffect, useRef, useState } from 'react'
import { Fade } from '@mui/material'
import { VIEWER_STYLE, MATTERPORT_CONTAINER } from './styles'
import { MpSdk, setupSdk, Model } from '@matterport/sdk'
import useMatterportRoomTracker from '../../Hooks/useMatterportTracker'
import AttributionOverlay from './AttributionOverlay'
import { useModelSettingsQuery } from './modelSettings.generated'

interface ModelViewerProps {
  matterportId: string
  matterportURLParams: { k: string; v: string }[]
}

const ModelViewer = ({ matterportId, matterportURLParams }: ModelViewerProps) => {
  const matterportEl = useRef<HTMLDivElement>(null)
  const [sdk, setSdk] = useState<MpSdk | null>(null)
  const [initialised, setInitialised] = useState<boolean>(false)
  const [modelDetails, setModelDetails] = useState<Model.ModelDetails | null>(null)

  const [{ data, fetching }] = useModelSettingsQuery({
    variables: {
      matterportId: matterportId,
    },
  })

  const { sendTrackingData } = useMatterportRoomTracker(sdk, matterportId)

  useEffect(() => {
    const setupMSdk = async () => {
      const additionalParams = matterportURLParams.reduce((acc, param) => {
        acc[param.k] = param.v
        return acc
      }, {} as Record<string, string>)

      const matterportSdk = (await setupSdk('4a08zz0ti44ecgcua1m6yt36c', {
        // TODO - get from env/window
        container: matterportEl.current as HTMLDivElement,
        space: matterportId as string,

        // param list available here: https://support.matterport.com/s/article/URL-Parameters?language=en_US
        // note, we can have deep links with ss=xxx and sr=yyy - this links to a specific view/camera point so we need to handle this too.
        iframeQueryParams: { qs: 0, play: 1, mls: 0, brand: 1, ...additionalParams },
      })) as MpSdk

      await matterportSdk.App.state.waitUntil(
        (state) => state.phase === matterportSdk.App.Phase.PLAYING
      )

      await matterportSdk.Link.setShareLinkPolicy(matterportSdk.Link.CreationPolicy.REFERRER, {
        includeParams: ['ss', 'sr', 'play', 'qs'],
      })

      setSdk(matterportSdk)
      setInitialised(true)

      const modelDetails = await matterportSdk.Model.getDetails()
      setModelDetails(modelDetails)
    }

    if (matterportEl.current && matterportId) {
      setupMSdk()
    }
  }, [matterportId, matterportURLParams])

  return (
    <>
      <div id='matterportContainer' style={VIEWER_STYLE}>
        <div style={MATTERPORT_CONTAINER} ref={matterportEl}></div>

        {!fetching && data?.getModelSettings.showEnquiryButtonByDefault && modelDetails && (
          <Fade in={initialised} timeout={800}>
            <div>
              <AttributionOverlay
                modelSettings={data.getModelSettings}
                modelDetails={modelDetails}
                matterportId={matterportId}
                sendTrackingData={sendTrackingData}
              />
            </div>
          </Fade>
        )}
      </div>
    </>
  )
}

export default ModelViewer
